/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/no-array-index-key */
import React, {
    FC, useCallback, useMemo, useState,
} from 'react';
import { useFormatting } from 'locale';
import {
    Button, Icon, Loader, Modal, Title, notification,
} from 'ui-library';
import Divider from 'ui-library/components/Divider';
import {
    ICON_ARROW_RIGHT_TRENDY,
    ICON_INFO_SLIM,
    ICON_LONG_ARROW_RIGHT,
    ICON_TYPE_BIN,
    ICON_TYPE_CLOSE,
    ICON_TYPE_CLOSE_SLIM,
} from 'ui-library/components/Icon';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import {
    EmptyFormsDocuments,
    THREEBTYPES,
    ZurichInvestEmailAddress,
} from 'constants/constants';
import {
    BUTTON_SIZE_SMALL,
    BUTTON_TYPE_LINK_WITH_ARROW,
} from 'ui-library/components/Button/constants';
import { useNavigate, useParams } from 'react-router-dom';
import {
    createCasePoAStorageKey,
    createNewCaseStorageKey,
} from 'datasource/CaseManagement/CreateCaseManager/CreateCaseManager';
import { getClientId } from 'utils';
import ServiceManager from 'services/ServiceManager';
import { EmptyFormsDocument } from 'datasource/useApplicationDetails/ApplicationDetailsManager.types';
import PlanDataChangeFormModal from 'components/PlanDataChangeFormModal/PlanDataChangeFormModal';
import { saveBase64File } from 'utils/file';
import { getOrderType } from 'pages/ClientOverview/pages/Portfolios/pages/SinglePortfolioView/pages/Cases/components/CasesTable/adaptor';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { getSetupData } from 'additiv-services/setup';
import {
    ApplicationTypesEnum,
    SingleApplicationRowProps,
} from './Applications.types';
import {
    ApplicationStatus,
    applicationStatusIgnoreListForReUpload,
    CaseOrderStatus,
    DocStatus,
    DocTypes,
} from '../../DocBox/DocBox.types';
import DocBox from '../../DocBox';
import Warning from '../Warning';
import { ApplicationDocumentType } from '../AdvisorySection/AdvisorySection.types';
import { useApplicationDetails } from '../../../datasource/useApplicationDetails';

const setupData = getSetupData();

const SingleApplicationRow: FC<SingleApplicationRowProps> = (props) => {
    const {
        t,
        i18n: { language },
    } = useTranslation();
    const { getAmountWithDecimals } = useFormatting();
    const navigate = useNavigate();
    const { dfsClientId, containerId } = useParams();
    const contactId = getClientId(dfsClientId);

    const emptyFormDocuments: EmptyFormsDocument[] = props?.emptyFormDocuments || [];

    const {
        id,
        status,
        type,
        typeKey,
        productName,
        productNameTranslated,
        productExternalId,
        applicationId,
        createdDate,
        validTill,
        isApplicationExpired,
        advisor,
        createdBy,
        investmentAmount,
        submitted,
        submittedBy,
        depotNumber,
        amountToBeSold,
        purchaseAmount,
        currentStrategy,
        documents,
        partialSaleOrLiquidate,
        rubrik,
        canBeDeleted,
        isSentCase,
        isSubmissionLocked,
        portfolioId,
        caseStatus,
        caseId,
        caseInvestmentPlan,
        caseOrderTypeId,
        isFollowUpInvestorProfile,
        isSubmissionReviewPending,
    } = props?.data || {};

    const [cancelInProgress, setCancelInProgress] = useState(false);
    const [isApplicationInSubmit, setApplicationInSubmit] = useState(false);
    const [isCloseZKPLeadReminderOpen, setIsCloseZKPLeadReminderOpen] = useState<boolean>(false);
    const {
        isAdvisoryDocReadyForSubmission,
        cancelApplication,
        submitApplication,
        onManualSign,
        onESign,
        onDocGeneration,
        onDocGenerationWithNewAPI,
        setRetryState,
        acceptDocumentSend,
        is3BDocGenerationInProgress,
        isUploadedAdvisory,
    } = useApplicationDetails();

    const submitButtonDisabledStatus = useMemo(
        () => documents?.find(
            (doc: ApplicationDocumentType) => doc.status !== DocStatus.ReadyToSubmit,
        )
            || !isAdvisoryDocReadyForSubmission
            || isApplicationInSubmit
            || isApplicationExpired
            || (!props?.is3aVB
                && typeKey === ApplicationTypesEnum.NEW
                && !props?.isAllIdDocumentsSigned),
        [
            documents,
            isAdvisoryDocReadyForSubmission,
            isApplicationInSubmit,
            isApplicationExpired,
            props?.isAllIdDocumentsSigned,
            props?.is3aVB,
            typeKey,
        ],
    );
    const showWarningStatus = useMemo(
        () => !documents?.find(
            (doc: ApplicationDocumentType) => doc.status !== DocStatus.ReadyToSubmit,
        )
            && (!isAdvisoryDocReadyForSubmission
                || (!props?.is3aVB
                    && typeKey === ApplicationTypesEnum.NEW
                    && !props?.isAllIdDocumentsSigned)),
        [
            documents,
            isAdvisoryDocReadyForSubmission,
            props?.isAllIdDocumentsSigned,
            props?.is3aVB,
            typeKey,
        ],
    );
    const allDocumentsGenerated = useMemo(
        () => !documents?.find(
            (doc: ApplicationDocumentType) => doc.status === DocStatus.ReadyToGenerate,
        ),
        [documents],
    );

    const showSubmitButtonCommon = useMemo(
        () => typeKey !== ApplicationTypesEnum.BUY
            && !partialSaleOrLiquidate
            && allDocumentsGenerated
            && !(
                typeKey === ApplicationTypesEnum.NEW && props?.isAdvisoryExpired
            )
            && status !== ApplicationStatus.canceled
            && status !== ApplicationStatus.revoked
            && status !== ApplicationStatus.completed
            && status !== ApplicationStatus.submitted
            && status !== ApplicationStatus.closed
            && status !== ApplicationStatus.advisoryInReview
            && !isSubmissionLocked
            && !(isSubmissionReviewPending && !props?.hasAccessToReUpload),
        [
            props?.isAdvisoryExpired,
            typeKey,
            allDocumentsGenerated,
            status,
            isSubmissionLocked,
            partialSaleOrLiquidate,
            isFollowUpInvestorProfile,
            props?.canCompleteFollowUpInvestorProfileApplication,
            isSubmissionReviewPending,
            props?.hasAccessToReUpload,
        ],
    );

    const showSubmitButtonForFollowUpInvestorProfile = useMemo(
        () => status !== ApplicationStatus.completed && isFollowUpInvestorProfile && props?.canCompleteFollowUpInvestorProfileApplication,
        [status, isFollowUpInvestorProfile, props?.canCompleteFollowUpInvestorProfileApplication],
    );

    const showSubmitButtonForVIOInUploadAdvisoryCase = useMemo(
        () => {
            const isVIO = props?.hasAccessToReUpload;
            const isApplicationSubmitted = status === ApplicationStatus.submitted;
            const showForVIO = isVIO && !isApplicationSubmitted && isSubmissionReviewPending;
            const showForNonVIO = !isVIO && !isApplicationSubmitted && !isSubmissionReviewPending;
            const isBuySell = typeKey === ApplicationTypesEnum.BUY || typeKey === ApplicationTypesEnum.SELL;

            return status !== ApplicationStatus.completed && !isBuySell && isUploadedAdvisory && (showForVIO || showForNonVIO);
        },
        [
            props?.hasAccessToReUpload,
            isUploadedAdvisory,
            status,
            isSubmissionReviewPending,
            typeKey,
        ],
    );

    const showSubmitButton = showSubmitButtonCommon || showSubmitButtonForFollowUpInvestorProfile || showSubmitButtonForVIOInUploadAdvisoryCase;

    const documentsClassNames = classnames('single-application--grid-col', {
        'doc-wrapper-transfer': isSentCase,
    });
    const submissionClassNames = classnames('single-application--grid-col', {
        'doc-submit-wrapper-transfer': isSentCase,
    });

    const isApplicationDisabled = useMemo(
        () => (typeKey === ApplicationTypesEnum.NEW
                && props?.isAdvisoryExpired && ![ApplicationStatus.submitted, ApplicationStatus.completed]?.includes(status))
            || isSubmissionLocked,
        [typeKey, props?.isAdvisoryExpired, isSubmissionLocked, status],
    );
    const isApplicationClosed = useMemo(
        () => status === ApplicationStatus.closed,
        [status],
    );

    const onCancelClick = useCallback(() => {
        Modal.confirm({
            title: t('dashboard.overviewClientApplications.confirmTitle'),
            content: t('dashboard.overviewClientApplications.confirmMessage'),
            okText: cancelInProgress ? (
                <Loader />
            ) : (
                t('dashboard.overviewClientApplications.confirmOk')
            ),
            cancelText: t('dashboard.overviewClientApplications.confirmCancel'),
            onOk: async () => {
                setCancelInProgress(true);
                await cancelApplication({ applicationId: id });
                setCancelInProgress(false);
            },
        });
    }, [cancelApplication, id]);

    const handleSubmitClick = useCallback(async () => {
        setApplicationInSubmit(true);
        try {
            await submitApplication({
                applicationId: id,
                planType: typeKey,
                productExternalId,
            });
        } finally {
            setApplicationInSubmit(false);
            if (isFollowUpInvestorProfile) {
                setIsCloseZKPLeadReminderOpen(true);
            }
        }
    }, [submitApplication, id, typeKey, isFollowUpInvestorProfile, productExternalId]);

    const allowDocumentSigning = useCallback(
        (document: ApplicationDocumentType) => !(props?.is3aVB && typeof document?.isSent === 'boolean'),
        [props?.is3aVB],
    );

    const handleFinalizeSellOrder = useCallback(() => {
        sessionStorage.removeItem(createNewCaseStorageKey);
        sessionStorage.removeItem(createCasePoAStorageKey);
        if (dfsClientId && portfolioId && amountToBeSold && id) {
            if (caseId) {
                navigate(
                    `/client/${dfsClientId}/case/${caseId}?containerId=${containerId}`,
                );
            } else {
                navigate(
                    `/client/${dfsClientId}/portfolios/${portfolioId}/new-case/type-of-order?investmentApplicationId=${id}&amount=${amountToBeSold}`,
                );
            }
        }
    }, [dfsClientId, portfolioId, id, amountToBeSold, caseId, containerId]);

    const {
        data: portfolioDataRaw,
    } = useGetCustomService({
        service: 'portfolioManagement',
        api: 'getPortfolioDetails',
        payload: [portfolioId, contactId, { language }],
        enabled: !!portfolioId && !!contactId && !!language,
        refetchOnWindowFocus: false,
    });

    const buySellInvestmentAmount = portfolioDataRaw?.data?.CurrentValue || 0;
    const applicationExpired = +status === ApplicationStatus.expired;
    const isAzpOrAzpOld = useMemo(
        () => (productExternalId
                && [
                    THREEBTYPES.azpAlt,
                    THREEBTYPES.azpNeu,
                    THREEBTYPES.azpNeuSB,
                ]?.includes(productExternalId))
            || false,
        [productExternalId],
    );
    const canFinalizeSellOrder = useMemo(
        () => typeKey === ApplicationTypesEnum.SELL
            && ![CaseOrderStatus.Submitted, CaseOrderStatus.Cancelled]?.includes(
                caseStatus,
            )
            && ![
                ApplicationStatus.canceled,
                ApplicationStatus.revoked,
                ApplicationStatus.closed,
                ApplicationStatus?.expired,
            ]?.includes(status),
        [typeKey, caseStatus, status],
    );

    const [emptyFormSendRetentionEmail, setEmptyFormSendRetentionEmail] = useState(false);
    const [emptyFormDownloading, setEmptyFormDownloading] = useState(false);
    const [planDataChangeFormModalVisible, setPlanDataChangeFormModalVisible] = useState(false);

    const emptyFormDocumentName = useMemo(
        () => emptyFormDocuments?.filter(
            (doc) => doc.dmsMetadataReferenceId
                    === EmptyFormsDocuments.PlanDataChangeForm,
        )?.[0]?.documentName || '-',
        [emptyFormDocuments],
    );
    const emptyFormDocumentDownloadPayload = useMemo(
        () => ({
            documentReferenceId: EmptyFormsDocuments.PlanDataChangeForm,
            portfolioId: portfolioId || 0,
            contactId: contactId as number,
            triggerRetentionEmail: emptyFormSendRetentionEmail,
        }),
        [
            portfolioId,
            emptyFormSendRetentionEmail,
            EmptyFormsDocuments.PlanDataChangeForm,
            contactId,
        ],
    );

    const closeIcon = useMemo(
        () => <Icon type={ICON_TYPE_CLOSE_SLIM} size={20} />,
        [],
    );
    const planDataChangeFormModalResources = useMemo(
        () => ({
            modalClass: '',
            title: t(
                'CaseManagementPage.Modal.PlanDataChangeDownloadFormTitle',
            ),
            okText: t('confirmation.download'),
            cancelText: t('confirmation.cancel'),
            checkBoxLabel: t(
                'CaseManagementPage.Modal.PlanDataChangeFormCheckboxLabel',
            ),
            infoText: t('CaseManagementPage.AZP.DownloadDocumentFillAndSend', {
                email: ZurichInvestEmailAddress,
            }),
        }),
        [t],
    );

    const onPlanDataChangeFormModalShow = useCallback(() => {
        setPlanDataChangeFormModalVisible(true);
    }, [setPlanDataChangeFormModalVisible]);
    const onEmptyFormDownloadModalClose = useCallback(() => {
        setPlanDataChangeFormModalVisible(false);
        setEmptyFormSendRetentionEmail(false);
    }, [setPlanDataChangeFormModalVisible, setEmptyFormSendRetentionEmail]);

    const onEmptyFormDownloadModalAccept = useCallback(async () => {
        if (emptyFormDocumentName && emptyFormDocumentDownloadPayload) {
            setEmptyFormDownloading(true);
            try {
                const response = await ServiceManager.customCaseManagementServices(
                    'downloadEmptyForm',
                    [emptyFormDocumentDownloadPayload],
                ) as any;

                if (response?.data?.fileContent) {
                    await saveBase64File({
                        base64: response.data.fileContent,
                        name: `${emptyFormDocumentName}.pdf`,
                    });
                }
            } catch (err: any) {
                notification.open({
                    content: `${t('contactGroups.somethingWentWrong')} ${
                        err?.message
                    }`,
                    type: 'error',
                });
            } finally {
                setEmptyFormSendRetentionEmail(false);
                setEmptyFormDownloading(false);
            }
        }
        setPlanDataChangeFormModalVisible(false);
        setEmptyFormDownloading(false);
    }, [emptyFormDocumentName, emptyFormDocumentDownloadPayload, t]);

    const goToCaseOverview = useCallback(() => {
        navigate(`/client/${dfsClientId}/case/${caseId}`);
    }, [dfsClientId, caseId]);

    return (
        <>
            <div
                className={`single-application-section ${
                    isApplicationDisabled
                        ? 'single-application-section--disabled'
                        : ''
                }`}
            >
                {applicationExpired && (
                    <div className="mb-16">
                        <div className="single-application--grid-col">
                            <div className="doc-wrapper">
                                <Warning className="application-closed">
                                    {t('application.Expired')}
                                </Warning>
                            </div>
                        </div>
                    </div>
                )}

                {isApplicationClosed && (
                    <div className="mb-16">
                        <div className="single-application--grid-col">
                            <div className="doc-wrapper">
                                <Warning className="application-closed">
                                    {t('application.Closed')}
                                </Warning>
                            </div>
                        </div>
                    </div>
                )}

                <Title type="3">
                    <strong>{type}</strong>
                    {' '}
                    {isFollowUpInvestorProfile ? (
                        <>{t('application.InvestorProfileDocumentation')}</>
                    ) : (
                        <>
                            {productExternalId !== THREEBTYPES.pk
                                ? `${productNameTranslated} -`
                                : ''}
                            {' '}
                            {productName}
                            {rubrik ? ` <${rubrik}>` : ' '}
                        </>
                    )}
                    {canBeDeleted && !applicationExpired && (
                        <div className="ml-auto">
                            <Button
                                className="application-details-header--delete-btn"
                                type="link"
                                onClick={onCancelClick}
                            >
                                <Icon
                                    size={16}
                                    className="mt-3 mr-10"
                                    type={ICON_TYPE_BIN}
                                />
                                {t('applicationDetailsHeader.Delete')}
                            </Button>
                        </div>
                    )}
                </Title>

                <div className="application--row">
                    <div className="application-info-grid">
                        {/* Columns only shown if application type is NEW || CHANGE */}
                        {[
                            ApplicationTypesEnum.NEW,
                            ApplicationTypesEnum.CHANGE,
                            ApplicationTypesEnum.NEW_EP,
                            ApplicationTypesEnum.CHANGE_EP,
                            ApplicationTypesEnum.UPDATE,
                        ].includes(typeKey) && (
                            <>
                                <div className="application-info-grid-col">
                                    <Title type="5">
                                        {t(
                                            'ApplicationDetails.AdvisorySection.ProductApplications.ApplicationID',
                                        )}
                                    </Title>
                                    {applicationId}
                                </div>
                                <div className="application-info-grid-col">
                                    <Title type="5">
                                        {t(
                                            'ApplicationDetails.AdvisorySection.ProductApplications.Created',
                                        )}
                                    </Title>
                                    {createdDate}
                                </div>
                                <div className="application-info-grid-col">
                                    <Title type="5">
                                        {t(
                                            'ApplicationDetails.AdvisorySection.ValidTill',
                                        )}
                                    </Title>
                                    {validTill}
                                </div>
                                <Divider direction="vertical" />
                            </>
                        )}

                        <div className="application-info-grid-col">
                            <Title type="5">
                                {t(
                                    'ApplicationDetails.AdvisorySection.Advisor',
                                )}
                            </Title>
                            {advisor}
                        </div>
                        <div className="application-info-grid-col">
                            <Title type="5">
                                {t(
                                    'ApplicationDetails.AdvisorySection.ProductApplications.CreatedBy',
                                )}
                            </Title>
                            {createdBy}
                        </div>
                        {!isFollowUpInvestorProfile && (
                            <Divider direction="vertical" />
                        )}

                        {/* Columns only shown if application type is NEW */}
                        {(typeKey === ApplicationTypesEnum.NEW
                            || typeKey === ApplicationTypesEnum.NEW_EP) && (
                            <div className="application-info-grid-col">
                                <Title type="5">
                                    {t(
                                        'ApplicationDetails.AdvisorySection.ProductApplications.InvestmentAmount',
                                    )}
                                </Title>
                                {getAmountWithDecimals(investmentAmount || 0)}
                            </div>
                        )}

                        {/* Columns only shown if application type is CHANGE || SELL || BUY */}
                        {[
                            ApplicationTypesEnum.CHANGE,
                            ApplicationTypesEnum.CHANGE_EP,
                            ApplicationTypesEnum.SELL,
                            ApplicationTypesEnum.BUY,
                        ].includes(typeKey) && (
                            <div className="application-info-grid-col">
                                <Title type="5">
                                    {t(
                                        'ApplicationDetails.AdvisorySection.ProductApplications.DepotNumber',
                                    )}
                                </Title>
                                {depotNumber}
                            </div>
                        )}
                        {typeKey === ApplicationTypesEnum.SELL && (
                            <div className="application-info-grid-col">
                                <Title type="5">
                                    {t(
                                        'createNewCase.TypeOfOrder.Stepper.title',
                                    )}
                                </Title>
                                {caseOrderTypeId
                                    ? t(
                                        getOrderType({
                                            isPk:
                                                  productExternalId
                                                  === THREEBTYPES.pk,
                                            orderType: caseOrderTypeId,
                                        }),
                                    )
                                    : '-'}
                            </div>
                        )}

                        {/* Columns only shown if application type is CHANGE */}
                        {(typeKey === ApplicationTypesEnum.CHANGE
                            || typeKey === ApplicationTypesEnum.CHANGE_EP) && (
                            <div className="application-info-grid-col">
                                <Title type="5">
                                    {t(
                                        'ApplicationDetails.AdvisorySection.ProductApplications.CurrentStrategy',
                                    )}
                                </Title>
                                {currentStrategy}
                            </div>
                        )}

                        {/* Columns only shown if application type is SELL || BUY */}
                        {[
                            ApplicationTypesEnum.SELL,
                            ApplicationTypesEnum.BUY,
                        ].includes(typeKey)
                            && typeKey === ApplicationTypesEnum.SELL
                            && status !== ApplicationStatus.closed && (
                            <div className="application-info-grid-col">
                                <Title type="5">
                                    {t(
                                        'ApplicationDetails.AdvisorySection.ProductApplications.InvestedAmount',
                                    )}
                                </Title>
                                {getAmountWithDecimals(
                                    buySellInvestmentAmount || 0,
                                )}
                            </div>
                        )}

                        {/* Columns only shown if application type is SELL */}
                        {typeKey === ApplicationTypesEnum.SELL
                            && status !== ApplicationStatus.closed && (
                            <div className="application-info-grid-col">
                                <Title type="5">
                                    {t(
                                        'ApplicationDetails.AdvisorySection.ProductApplications.ToBeSold',
                                    )}
                                </Title>
                                {getAmountWithDecimals(amountToBeSold || 0)}
                            </div>
                        )}

                        {/* Columns only shown if application type is BUY */}
                        {typeKey === ApplicationTypesEnum.BUY && (
                            <div className="application-info-grid-col">
                                <Title type="5">
                                    {t(
                                        'ApplicationDetails.AdvisorySection.ProductApplications.PurchaseAmount',
                                    )}
                                </Title>
                                {getAmountWithDecimals(purchaseAmount || 0)}
                            </div>
                        )}

                        {typeKey === ApplicationTypesEnum.SELL
                            && status !== ApplicationStatus.closed && (
                            <>
                                <div className="application-info-grid-col">
                                    <Title type="5">
                                        {t(
                                            'createNewCase.TypeOfOrder.remainingAmount',
                                        )}
                                    </Title>
                                    {getAmountWithDecimals(
                                        (buySellInvestmentAmount || 0)
                                                - (amountToBeSold || 0),
                                    )}
                                </div>
                                {caseStatus === CaseOrderStatus.Submitted && (
                                    <div className="application-info-grid-col">
                                        <Title type="5">
                                            {t(
                                                'createNewCase.TypeOfOrder.CaseId',
                                            )}
                                        </Title>
                                        {caseId}
                                    </div>
                                )}
                            </>
                        )}

                        {!(
                            typeKey === ApplicationTypesEnum.SELL
                            && status !== ApplicationStatus.closed
                        ) && (
                            <>
                                <Divider direction="vertical" />
                                <div className="application-info-grid-col">
                                    <Title type="5">
                                        {t(
                                            'ApplicationDetails.AdvisorySection.ProductApplications.Submitted',
                                        )}
                                    </Title>
                                    {submitted || '-'}
                                </div>
                                <div className="application-info-grid-col">
                                    <Title type="5">
                                        {t(
                                            'ApplicationDetails.AdvisorySection.ProductApplications.SubmittedBy',
                                        )}
                                    </Title>
                                    {submittedBy || '-'}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="visible-ipad mb-16">
                        {showWarningStatus && (
                            <Warning>
                                {t(
                                    'ApplicationDetails.AdvisorySection.ProductApplications.WarningToUploadDoc',
                                )}
                            </Warning>
                        )}
                    </div>

                    {!(
                        typeKey === ApplicationTypesEnum.SELL
                        && status === ApplicationStatus.closed
                    )
                        && isAzpOrAzpOld
                        && caseInvestmentPlan
                        && !isFollowUpInvestorProfile
                        && !caseInvestmentPlan.isCaseSource && (
                        <>
                            <PlanDataChangeFormModal
                                width={480}
                                visible={planDataChangeFormModalVisible}
                                closable
                                hasBorderRadius
                                okButtonProps={{
                                    disabled: false,
                                    loading: emptyFormDownloading,
                                }}
                                closeIcon={closeIcon}
                                resources={planDataChangeFormModalResources}
                                // Event handlers
                                onOk={onEmptyFormDownloadModalAccept}
                                onCancel={onEmptyFormDownloadModalClose}
                                toggleChecked={emptyFormSendRetentionEmail}
                                onToggleChange={(checked) => {
                                    setEmptyFormSendRetentionEmail(checked);
                                }}
                            />
                            <div className="case-orders">
                                <div className="case-orders-section--grid">
                                    <div className="case-orders-section--grid-col">
                                        <DocBox
                                            status={status}
                                            isSentCase={isSentCase}
                                            type={DocTypes.EMPTY_FORM}
                                            isSent={false}
                                            uploadStatus={
                                                DocStatus.ReadyToReUpload
                                            }
                                            documentName={
                                                emptyFormDocumentName
                                            }
                                            applicationId={applicationId}
                                            documentId={undefined}
                                            allowSigning={false}
                                            applicationExpired={false}
                                            onPlanDataChangeFormModalShow={
                                                onPlanDataChangeFormModalShow
                                            }
                                            is3aVB={props?.is3aVB}
                                            isApplicationClosed={
                                                isApplicationClosed
                                            }
                                        />
                                    </div>
                                    <div className="case-orders-section--grid-col">
                                        <div className="plan-data-change-form-info">
                                            {
                                                planDataChangeFormModalResources.infoText
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {isAzpOrAzpOld
                        && caseInvestmentPlan
                        && caseInvestmentPlan?.isCaseSource
                        && canFinalizeSellOrder && (
                        <div className="flex flex-end">
                            <Button
                                type="secondary"
                                onClick={handleFinalizeSellOrder}
                                size={BUTTON_SIZE_SMALL}
                            >
                                {isApplicationInSubmit ? (
                                    <Loader />
                                ) : (
                                    <>
                                        {t(
                                            'ApplicationDetails.Button.FinalizeSalesOrder',
                                        )}
                                    </>
                                )}
                            </Button>
                        </div>
                    )}

                    {canFinalizeSellOrder
                        && !isAzpOrAzpOld
                        && caseInvestmentPlan?.isCaseSource && (
                        <div className="flex flex-end">
                            <Button
                                type="secondary"
                                onClick={handleFinalizeSellOrder}
                                size={BUTTON_SIZE_SMALL}
                            >
                                {isApplicationInSubmit ? (
                                    <Loader />
                                ) : (
                                    <>
                                        {t(
                                            'ApplicationDetails.Button.FinalizeSalesOrder',
                                        )}
                                    </>
                                )}
                            </Button>
                        </div>
                    )}

                    {caseStatus === CaseOrderStatus.Submitted
                        && status === ApplicationStatus.closed && (
                        <div className="flex flex-end">
                            <Button
                                type={BUTTON_TYPE_LINK_WITH_ARROW}
                                onClick={goToCaseOverview}
                                size={BUTTON_SIZE_SMALL}
                            >
                                {t(
                                    'ApplicationDetails.Button.GoToCaseOverview',
                                )}
                                <Icon
                                    className="ml-8"
                                    type={ICON_LONG_ARROW_RIGHT}
                                    size={24}
                                />
                            </Button>
                        </div>
                    )}

                    <div className="application-footer-grid">
                        <div className={documentsClassNames}>
                            {!(
                                typeKey === ApplicationTypesEnum.SELL
                                && status === ApplicationStatus.closed
                            )
                                && documents?.map((document, index) => (
                                    <DocBox
                                        status={status}
                                        isSentCase={isSentCase}
                                        key={`${document?.documentName?.replaceAll(
                                            ' ',
                                            '_',
                                        )}_${index}`}
                                        type={document.type}
                                        isSent={document?.isSent}
                                        uploadStatus={document?.status}
                                        documentName={document?.documentName}
                                        applicationId={document?.applicationId}
                                        documentId={document?.documentId}
                                        allowSigning={allowDocumentSigning(
                                            document,
                                        )}
                                        applicationExpired={applicationExpired}
                                        onManualSign={onManualSign}
                                        onESign={onESign}
                                        onDocGeneration={setupData?.UPLOAD_STATIC_DOCUMENT_FEATURE_FLAG ? onDocGenerationWithNewAPI : onDocGeneration}
                                        setRetryState={setRetryState}
                                        acceptDocumentSend={acceptDocumentSend}
                                        canESign={props?.hasDocumentESignAccess}
                                        is3aVB={props?.is3aVB}
                                        isApplicationClosed={
                                            isApplicationClosed
                                        }
                                        isFollowUpInvestorProfile={
                                            isFollowUpInvestorProfile
                                        }
                                        isDocGenerationInProgress={is3BDocGenerationInProgress}
                                        canReUpload={document.type === DocTypes.APPLICATION
                                                    && !applicationStatusIgnoreListForReUpload?.includes(status) && document?.status !== DocStatus.Error}
                                    />
                                ))}
                        </div>
                        {showSubmitButton && !applicationExpired && (
                            <div className={submissionClassNames}>
                                <div className="single-application--action">
                                    <div className="visible-desktop">
                                        {showWarningStatus && (
                                            <Warning>
                                                {t(
                                                    'ApplicationDetails.AdvisorySection.ProductApplications.WarningToUploadDoc',
                                                )}
                                            </Warning>
                                        )}
                                    </div>
                                    <Button
                                        type="primary"
                                        disabled={!!submitButtonDisabledStatus}
                                        onClick={handleSubmitClick}
                                    >
                                        {isApplicationInSubmit ? (
                                            <Loader />
                                        ) : (
                                            <>
                                                {t(
                                                    'ApplicationDetails.AdvisorySection.ProductApplications.Submit',
                                                )}
                                                <Icon
                                                    className="ml-8"
                                                    type={
                                                        ICON_ARROW_RIGHT_TRENDY
                                                    }
                                                    size={24}
                                                />
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {isSubmissionLocked && (
                            <Warning
                                className="submission-lock-warning"
                                iconType={ICON_INFO_SLIM}
                            >
                                {t(
                                    'ApplicationDetails.AdvisorySection.ProductApplications.WarningSubmissionLock',
                                )}
                            </Warning>
                        )}
                    </div>
                </div>
            </div>

            {isCloseZKPLeadReminderOpen && !props?.apiError && (
                <Modal
                    title=""
                    visible
                    okText={t('Modal.OkText.Understood')}
                    cancelText=""
                    onOk={() => setIsCloseZKPLeadReminderOpen(false)}
                    onCancel={() => setIsCloseZKPLeadReminderOpen(false)}
                    okType="primary"
                    width={610}
                    className="upload-document-modal"
                    borderRadius
                    closable
                    closeIcon={<Icon type={ICON_TYPE_CLOSE} size={16} />}
                >
                    <Title type={2} className="font-normal mb-32">
                        {t('ApplicationDetails.SingleApplicationRow.Modal.Title.CloseZKPLeadReminder')}
                    </Title>
                    <p>
                        {t('ApplicationDetails.SingleApplicationRow.Modal.Description.CloseZKPLeadReminder')}
                    </p>
                </Modal>
            )}
        </>
    );
};

export default SingleApplicationRow;
